/**
 * Domain vocabulary shared by the API and every client.
 *
 * The keys below are the canonical, language-independent values stored in the
 * database. Clients translate them for display; the server never does.
 */

/*
 * Fourteen, where there were eight.
 *
 * Eight was too few to sort a real statement into: a marketplace, a café, a
 * streaming service, a bank fee and an insurance premium had nowhere to go,
 * so they were forced into groceries or leisure, or piled up in Other. The
 * original eight keys are unchanged -- they are what every existing row is
 * stored under -- and the new ones sit between them in reading order.
 */
/*
 * The terms of service and privacy policy an account has to have accepted, by
 * the date of the version in force. Changing the documents means changing this:
 * every account signs in to a one-time prompt for the new version, and the
 * date printed at the top of each document has to say the same.
 */
export const TERMS_VERSION = '2026-09-28.3';

/*
 * Versions of the terms published ahead of the day they take effect, oldest
 * first. § 11 of the terms promises 14 days' notice of a change, so a new
 * version is shown from the day it is published, may be accepted early, and is
 * required only from `from` on. The date part of `version` is that day; the
 * part after the dot is the running number the document itself shows
 * ("WERSJA 5").
 *
 * More than one can be waiting: version 4 was published for 15 October when
 * version 5 (the new split of the plans) needed its own 14 days. Each later
 * version contains the earlier ones, so accepting the newest accepts them all,
 * and an account is asked once, not once per version.
 */
export const TERMS_UPCOMING = Object.freeze([
  Object.freeze({ version: '2026-10-15.4', from: '2026-10-15T00:00:00+02:00' }),
  Object.freeze({ version: '2026-10-25.5', from: '2026-10-25T00:00:00+02:00' })
]);

/** The privacy policy's own date: its changes apply when published. */
export const PRIVACY_VERSION = '2026-10-13';

const timeOf = now => (typeof now === 'number' ? now : Date.parse(now));

/** The version in force at a moment: the newest one whose day has come. */
export function termsVersionAt(now = Date.now()) {
  const time = timeOf(now);
  const live = TERMS_UPCOMING.filter(next => time >= Date.parse(next.from));
  return live.length ? live[live.length - 1].version : TERMS_VERSION;
}

/** The newest published version still waiting for its day, or null. */
export function termsPendingAt(now = Date.now()) {
  const time = timeOf(now);
  const waiting = TERMS_UPCOMING.filter(next => time < Date.parse(next.from));
  return waiting.length ? waiting[waiting.length - 1] : null;
}

/*
 * Whether an accepted version covers a required one. Versions are named by
 * date, so the later string is the later version; a later one includes all
 * that came before it.
 */
export function termsCover(accepted, required) {
  return Boolean(accepted) && String(accepted) >= String(required);
}

export const CATEGORIES = [
  'Housing', 'Groceries', 'Dining', 'Shopping', 'Vehicle', 'Transport', 'Utilities',
  'Subscriptions', 'Health', 'Insurance', 'Leisure', 'Fees', 'Debt service', 'Other'
];

/*
 * A category of one's own -- "Prezenty", "Zwierzęta" -- beside the fixed list.
 *
 * Only for what a person types in by hand: an expense and a fixed cost. The
 * import, the merchant dictionary and the AI suggestions keep to the fixed
 * list, and so does anything leaving the account (see the weekly review),
 * because a name somebody typed is theirs and says more than a key does.
 */
export const CUSTOM_CATEGORY_MAX = 40;

/** A fixed category key, a valid name of one's own (trimmed), or null. */
export function expenseCategory(value) {
  if (typeof value !== 'string') return null;
  if (CATEGORIES.includes(value)) return value;
  const name = value.replace(/\s+/g, ' ').trim();
  if (!name || name.length > CUSTOM_CATEGORY_MAX) return null;
  /* No control characters, and nothing that reads as markup. */
  if (/[\u0000-\u001f\u007f<>]/.test(name)) return null;
  /* A spelling of a fixed key is that key, not a second category beside it. */
  const fixed = CATEGORIES.find(key => key.toLowerCase() === name.toLowerCase());
  return fixed || name;
}

export const isCustomCategory = value => typeof value === 'string' && !CATEGORIES.includes(value);

export const ASSET_CLASSES = [
  /*
   * These strings are stored on every holding, so they are identifiers first
   * and wording second. `Bonds / cash` reads as "Obligacje" on screen now --
   * there is a separate Cash class and naming both of them cash was the reason
   * people could not tell which to pick -- but the stored value is left exactly
   * as it is. Renaming it would mean rewriting every row that already carries
   * it, to change nothing anybody sees.
   */
  'Equity / ETF', 'Crypto', 'Retirement', 'Bonds / cash', 'Real estate',
  /*
   * Metal held as metal: an ounce of gold, a bag of silver.
   *
   * It was going under Equity / ETF, beside shares, which is where it stops
   * being answerable -- an allocation chart cannot say what share of a
   * portfolio is in metal if metal is filed as equity. The quote path already
   * knew the difference (XAU and XAG are looked up as the front-month future,
   * not as the tickers that happen to share those letters); this is the ledger
   * catching up with it.
   */
  'Precious metals',
  /*
   * Cash held as an allocation, not money in a current account -- that lives in
   * the savings balances instead.
   *
   * Its own class because units and an average cost mean nothing here. Holding
   * 205 dollars is not "one unit bought at 205": there is no purchase price to
   * beat and no quantity to hold. What such a position actually loses is
   * purchasing power, so that is what its gain column reports.
   */
  'Cash'
];

/** Classes entered as a plain amount, with no units and no cost basis. */
export const AMOUNT_ONLY_CLASSES = ['Cash'];

export const isAmountOnly = cls => AMOUNT_ONLY_CLASSES.includes(cls);

export const CAR_EXPENSE_TYPES = [
  'Maintenance', 'Repair', 'Insurance', 'Registration', 'Tyres', 'Loan payment',
  /* The periodic roadworthiness test (przegląd techniczny): due again a year on. */
  'Inspection',
  /*
   * Last, and deliberately last: it is the bucket for a cost the five names
   * above do not fit, not a default. A vehicle attracts charges nobody
   * anticipated -- a car wash, a parking fine, a motorway vignette -- and
   * without somewhere to put them they were being filed as Maintenance, which
   * is worse than admitting the category is unknown.
   */
  'Other'
];

export const TABS = ['overview', 'cash', 'debt', 'invest', 'car', 'journal', 'tax'];

/*
 * Tax summaries nobody has built yet, which an account can ask for in the
 * Taxes tab -- one tap each, counted in the admin panel, so the next country
 * is the one people need. The Netherlands is listed but not votable: Box 3
 * taxes wealth, not trades, so there is nothing to work out from a journal.
 */
export const TAX_VOTE_KEYS = Object.freeze(['ca', 'au']);

/*
 * The trading journal's vocabularies.
 *
 * Deliberately closed lists, like the expense categories: the point of a
 * journal is that last April's entries can be compared with this April's, and
 * free text does not compare. `SESSION`, `TIME FRAME` and `Setup` stay free
 * text because those genuinely differ per trade.
 */
export const TRADE_SIDES = ['LONG', 'SHORT'];
export const TRADE_EXITS = ['TP hit', 'SL hit', 'BE', 'Manual'];
export const TRADE_RISKS = ['FULL', 'HALF', 'QUARTER'];

/*
 * Whether a journal trade belongs in a capital-gains summary: PIT-38, Form
 * 8949 / 6781, Anlage KAP, SA108.
 *
 * Not a backtest, which never happened. And not a trade taken on a prop
 * account: the trader owns no position there -- the account is the firm's, and
 * often simulated -- so its results are not a disposal of anything. What is
 * taxed is the payout the firm sends, and in every country covered that is
 * income of another kind (PIT-36/37 in Poland, Schedule C in the US, income
 * tax rather than CGT in the UK, Gewerbebetrieb or sonstige Einkünfte in
 * Germany). The payout is recorded as income when it arrives, from the prop
 * account's own screen; counting the trades as well would tax the same money
 * twice, under the wrong rules.
 */
export const isTaxableTrade = row => !row.backtestId && !row.propAccountId;

/*
 * Prop-firm money. What an evaluation costs -- the fee, a reset, an
 * activation -- and what a funded account returns: payouts, and the fee
 * refunded with the first one, as many firms do.
 */
export const PROP_COSTS = ['fee', 'reset', 'activation'];
export const PROP_RETURNS = ['payout', 'refund'];
export const PROP_KINDS = [...PROP_COSTS, ...PROP_RETURNS];
/* Phase 2: the second step of a two-step evaluation, between the challenge and funded. */
export const PROP_STATUSES = ['challenge', 'phase2', 'funded', 'failed', 'closed'];

export const TRADE_SIDE_COLORS = { LONG: '#3DD9A0', SHORT: '#EF4E43' };
export const TRADE_EXIT_COLORS = {
  'TP hit': '#3DD9A0',
  'SL hit': '#EF4E43',
  BE: '#54697D',
  Manual: '#6FE8BC'
};

export const DEBT_STRATEGIES = ['rate', 'balance'];

/*
 * A loan is paid down by its instalments, and a month ahead is projected from
 * them. A card's balance changes only when a payment or a correction says so:
 * what will be spent on it is not known, so nothing is projected for it.
 */
export const DEBT_KINDS = ['loan', 'card'];

/*
 * Metal held as metal: bars and coins, weighed in troy ounces or in grams.
 *
 * Paper metal -- an ETF, an ETC, a certificate -- is a ticker like any other
 * and is priced per share. Physical metal is priced from the metal itself,
 * which is quoted per troy ounce; a holding weighed in grams keeps that price
 * and is valued at it divided by the grams in an ounce. Stored prices stay per
 * ounce whatever the holding's unit, so a refresh or a filled history needs no
 * special case: only the valuation converts.
 */
export const METAL_FORMS = ['paper', 'physical'];
export const METAL_UNITS = ['oz', 'g'];
export const TROY_OUNCE_GRAMS = 31.1034768;
export const METAL_TICKERS = ['XAU', 'XAG', 'XPT', 'XPD'];

/*
 * Fineness: the share of the weight that is the metal itself -- 0.999 for a
 * bullion bar, 0.925 for sterling silver, 0.9167 for a 22-carat coin. The
 * amount entered is what the piece weighs; it is worth its pure content, so
 * the ounce price is scaled by the fineness too. None recorded means pure.
 * The common ones per metal, investment grade first; anything else is typed in.
 */
export const METAL_PURITIES = {
  XAU: [0.9999, 0.999, 0.986, 0.9167, 0.9, 0.75, 0.585, 0.333],
  XAG: [0.9999, 0.999, 0.958, 0.925, 0.9, 0.835, 0.8, 0.5],
  XPT: [0.9995, 0.95, 0.9],
  XPD: [0.9995, 0.95, 0.5]
};
/** Carats for the gold finenesses that go by them. */
export const GOLD_CARATS = { 0.9999: 24, 0.9167: 22, 0.75: 18, 0.585: 14, 0.333: 8 };
export const metalPurity = holding => {
  const value = Number(holding?.purity);
  return value > 0 && value <= 1 ? value : 1;
};

/** What one stored (per-ounce, pure) price is worth per unit the holding is weighed in. */
export const metalUnitFactor = holding => (holding?.unit === 'g' ? 1 / TROY_OUNCE_GRAMS : 1) * metalPurity(holding);

/**
 * Money.
 *
 * Every amount is stored exactly as it was entered, alongside the currency it
 * was entered in — never pre-converted. Rates move; a ledger that rewrote its
 * own history every time they did would be worthless. Conversion happens at
 * read time, into the account's base currency.
 */
export const DEFAULT_BASE_CURRENCY = 'PLN';

/** Offered in the pickers. Any other ISO 4217 code is still accepted. */
export const SUGGESTED_CURRENCIES = [
  'PLN', 'EUR', 'USD', 'GBP', 'CHF', 'CZK', 'SEK', 'NOK', 'DKK', 'HUF', 'JPY', 'CAD', 'AUD'
];

/*
 * The currency a new account starts in, from where its owner is.
 *
 * Every account used to start in złoty, wherever it was opened -- so somebody
 * in Ohio typed their rent in, read "PLN 1,750" back and had to find the
 * setting before the app made sense. `country` is the one the browser's time
 * zone points to (shared/timezones.js); a place with none of the offered
 * currencies starts in dollars, or in złoty when the app is in Polish. Only a
 * starting point: Preferences change it at any time.
 */
const EURO_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT',
  'SK', 'SI', 'ES', 'MC', 'SM', 'VA', 'AD', 'ME', 'XK', 'GF', 'GP', 'MQ', 'RE', 'YT', 'MF', 'BL', 'PM', 'AX'
];
const HOME_CURRENCY = {
  PL: 'PLN', US: 'USD', PR: 'USD', GU: 'USD', VI: 'USD', AS: 'USD', MP: 'USD',
  GB: 'GBP', IM: 'GBP', JE: 'GBP', GG: 'GBP', CH: 'CHF', LI: 'CHF', CZ: 'CZK', SE: 'SEK', NO: 'NOK', SJ: 'NOK',
  DK: 'DKK', FO: 'DKK', GL: 'DKK', HU: 'HUF', JP: 'JPY', CA: 'CAD', AU: 'AUD'
};

/* Where miles and gallons are what the pump and the odometer say. */
const IMPERIAL_COUNTRIES = ['US', 'PR', 'GU', 'VI', 'AS', 'MP', 'LR', 'MM'];
export const homeUnits = country => (IMPERIAL_COUNTRIES.includes(String(country || '').toUpperCase()) ? 'imperial' : 'metric');

export function homeCurrency(country, language = 'EN') {
  const code = String(country || '').toUpperCase();
  if (EURO_COUNTRIES.includes(code)) return 'EUR';
  return HOME_CURRENCY[code] || (language === 'PL' ? 'PLN' : 'USD');
}

export const isCurrencyCode = value => /^[A-Z]{3}$/.test(String(value || ''));

export const normaliseCurrency = value => String(value || '').trim().toUpperCase();

/**
 * The symbols the app used before currencies had codes, mapped to the code they
 * almost certainly meant. Used once, when upgrading a database.
 */
export const SYMBOL_TO_CURRENCY = {
  $: 'USD', 'US$': 'USD', '€': 'EUR', '£': 'GBP', 'zł': 'PLN', 'PLN': 'PLN',
  'Kč': 'CZK', 'CHF': 'CHF', 'kr': 'SEK', '¥': 'JPY', 'Ft': 'HUF'
};

/**
 * Converts between two currencies, via the base.
 *
 * Rates are stored against the base — how many of the base one unit buys — so
 * going from one non-base currency to another means through it: to base, then
 * out again. Returns null when either leg has no rate, which is the same answer
 * the rest of this file gives when it does not know: excluded and reported,
 * never guessed at par.
 *
 * Used to price a holding quoted in one currency and kept in another. A silver
 * position kept in zloty is quoted in dollars per ounce by every provider that
 * has it; refusing that outright left the position permanently unpriced, and
 * writing it through unconverted read 67.79 zl for a 67.79 dollar price.
 */
export function convertBetween(amount, from, to, base, rates) {
  const source = normaliseCurrency(from) || base;
  const target = normaliseCurrency(to) || base;
  if (source === target) return amount;

  const inBase = toBase(amount, source, base, rates);
  if (inBase === null) return null;
  if (target === base) return inBase;

  const rate = rates && rates[target];
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return inBase / rate;
}

/**
 * Converts an amount into the base currency.
 *
 * `rates` maps a currency code to how many units of base one unit of it buys.
 * A missing rate returns null rather than guessing: silently treating an
 * unconverted dollar as a złoty would corrupt every total that touched it.
 */
export function toBase(amount, currency, base, rates) {
  const from = normaliseCurrency(currency) || base;
  if (from === base) return amount;

  const rate = rates && rates[from];
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return amount * rate;
}

/**
 * The rate in force for a given month.
 *
 * Rates are a step function: one entered against a month stays in force until
 * the next one. Two rules make that usable rather than pedantic -- an undated
 * rate (month '') is the standing one, used wherever no dated rate applies, and
 * the earliest dated rate also covers everything before it, since a rate curve
 * that simply stops has nothing better to offer a month further back.
 *
 * What it will not do is use August's rate for March when March has its own.
 * Re-valuing a closed month at today's rate is how a ledger starts reporting a
 * gain that never happened.
 */
/**
 * The row in force for a month, from rows already narrowed to one subject.
 *
 * Exchange rates and share prices are the same problem wearing different field
 * names: a value that changes over time, recorded when somebody looked, and
 * asked for as of a month that may fall between two records. One
 * implementation, because the last time these rules were written down twice the
 * second copy disagreed with the first on screen.
 */
function inForceRow(rows, month) {
  if (!rows.length) return null;

  const dated = rows
    .filter(row => isMonth(row.month))
    .sort((a, b) => a.month.localeCompare(b.month));

  const inForce = [...dated].reverse().find(row => row.month <= month);
  if (inForce) return inForce;

  const standing = rows.find(row => !isMonth(row.month));
  if (standing) return standing;

  return dated.length ? dated[0] : null;
}

export function rateRowForMonth(history, currency, month) {
  const code = normaliseCurrency(currency);
  return inForceRow(
    (history || []).filter(
      row => normaliseCurrency(row.currency) === code && Number.isFinite(row.rate) && row.rate > 0
    ),
    month
  );
}

/** The same question for a holding: what was this worth in that month? */
export function priceRowForMonth(history, ticker, month) {
  const key = String(ticker || '').trim().toUpperCase();
  return inForceRow(
    (history || []).filter(
      row => String(row.ticker || '').trim().toUpperCase() === key
        && Number.isFinite(row.price) && row.price > 0
    ),
    month
  );
}

export function priceForMonth(history, ticker, month) {
  const row = priceRowForMonth(history, ticker, month);
  return row ? row.price : null;
}

/**
 * The rate itself.
 *
 * A thin wrapper on purpose: the resolution rules live in exactly one place.
 * A view that worked them out for itself once put "in force since Mar 2026"
 * next to a rate that came from the standing row, which is how this got split.
 */
export function rateForMonth(history, currency, month) {
  const row = rateRowForMonth(history, currency, month);
  return row ? row.rate : null;
}

/** The whole history flattened to the map `toBase` wants for one month. */
/**
 * The rate history restated against a new base currency.
 *
 * Rates say how many units of the base one unit of a currency buys, so a
 * change of base changes every number: each month's rates are divided by what
 * the incoming base was worth then, and the outgoing base becomes a currency
 * like any other, at the inverse. Shared by the server, which stores the
 * result, and the app, which shows it the moment the setting changes -- before,
 * it read the old złoty rates as euro ones until the page was reloaded.
 */
export function rebaseRateHistory(history, from, to) {
  const rows = history || [];
  if (!rows.length || !from || !to || from === to) return rows;
  const months = [...new Set(rows.map(row => row.month))];
  const restated = [];
  for (const month of months) {
    /* What one unit of the incoming base was worth in the outgoing one. */
    const pivot = rateForMonth(rows, to, month);
    if (!Number.isFinite(pivot) || pivot <= 0) continue;
    for (const row of rows.filter(r => r.month === month)) {
      // The new base needs no rate against itself.
      if (normaliseCurrency(row.currency) === to) continue;
      restated.push({ ...row, rate: row.rate / pivot });
    }
    /* The outgoing base is now just another currency, and needs a rate. */
    restated.push({ currency: from, month, rate: 1 / pivot, source: 'auto' });
  }
  return restated;
}

export function resolveRates(history, month) {
  const out = {};
  for (const row of history || []) {
    const code = normaliseCurrency(row.currency);
    if (code && !(code in out)) {
      const rate = rateForMonth(history, code, month);
      if (rate !== null) out[code] = rate;
    }
  }
  return out;
}

/**
 * Vehicle units.
 *
 * Storage is always metric — kilometres and litres — and the imperial view is a
 * conversion at the edges. Picking one canonical unit is what stops a ledger
 * from quietly mixing the two after somebody changes the setting.
 */
/**
 * A VIN as stored: upper case, no spaces or dashes. '' for none, null when
 * what was given cannot be one.
 *
 * Seventeen characters, never I, O or Q -- those were left out of the VIN
 * alphabet so they could not be misread as 1 and 0. Shared, so the form can
 * refuse a mistyped VIN before anything is sent anywhere.
 */
export function normaliseVin(raw) {
  if (raw === null || raw === undefined || raw === '') return '';
  if (typeof raw !== 'string') return null;
  const vin = raw.replace(/[\s-]/g, '').toUpperCase();
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin) ? vin : null;
}

export const UNIT_SYSTEMS = ['metric', 'imperial'];

export const KM_PER_MILE = 1.609344;
export const LITRES_PER_US_GALLON = 3.785411784;

export const kmToMiles = km => km / KM_PER_MILE;
export const milesToKm = miles => miles * KM_PER_MILE;
export const litresToGallons = litres => litres / LITRES_PER_US_GALLON;
export const gallonsToLitres = gallons => gallons * LITRES_PER_US_GALLON;

/**
 * Fuel economy, in the unit each system actually uses. They run in opposite
 * directions: more miles per gallon is better, fewer litres per 100 km is
 * better — so views must not compare them as if they were the same number.
 */
export function economyFrom(km, litres, system) {
  if (!(km > 0) || !(litres > 0)) return null;
  return system === 'imperial'
    ? kmToMiles(km) / litresToGallons(litres)     // mpg
    : (litres / km) * 100;                        // L/100 km
}

/** True when a bigger number is the better one. */
export const economyHigherIsBetter = system => system === 'imperial';

export const CATEGORY_COLORS = {
  Housing: '#3DD9A0',
  Groceries: '#2FAF83',
  Dining: '#E0A458',
  Shopping: '#5AA9E6',
  Vehicle: '#EF4E43',
  Transport: '#F08A5D',
  Utilities: '#6FE8BC',
  Subscriptions: '#9B8AFB',
  Health: '#8296A8',
  Insurance: '#B6C8D6',
  Leisure: '#1E7A5F',
  Fees: '#C77DA0',
  'Debt service': '#C0392E',
  Other: '#54697D'
};

export const CAR_TYPE_COLORS = {
  Maintenance: '#3DD9A0',
  Repair: '#EF4E43',
  Insurance: '#6FE8BC',
  Registration: '#54697D',
  Tyres: '#2FAF83',
  'Loan payment': '#C0392E',
  Inspection: '#8FB3C9',
  Other: '#3E5164'
};

export const CLASS_PALETTE = ['#3DD9A0', '#2FAF83', '#1E7A5F', '#6FE8BC', '#54697D', '#3E5164'];

export const PALETTE = {
  bg: '#070C12',
  panel: '#0A1119',
  card: '#0D1620',
  line: '#16202C',
  lineSoft: '#101922',
  stroke: '#1E2A38',
  strokeHi: '#2C3D50',
  text: '#F2EFE9',
  textDim: '#C6D2DC',
  muted: '#8296A8',
  faint: '#54697D',
  ghost: '#3E5164',
  accent: '#3DD9A0',
  accentHi: '#6FE8BC',
  accentDeep: '#1E4A3B',
  danger: '#EF4E43',
  /*
   * Liquidity+ gold, from the premium logo design. Kept to the marks that say
   * "this account has Liquidity+": green and red stay the colours of money.
   */
  gold: '#E3C27A',
  goldDeep: '#5C4B25',
  goldBg: '#231E14'
};

export const DEFAULT_SETTINGS = {
  language: 'EN',
  currency: '$',
  hideBalances: false,
  startTab: 'overview',
  debtStrategy: 'rate',
  unitSystem: 'metric',
  baseCurrency: DEFAULT_BASE_CURRENCY
};

/** `2026-08` -> `2026-09` (delta may be negative). */
export function shiftMonth(month, delta) {
  const [ys, ms] = String(month).split('-');
  let year = parseInt(ys, 10);
  let mon = parseInt(ms, 10) - 1 + delta;
  year += Math.floor(mon / 12);
  mon = ((mon % 12) + 12) % 12;
  return year + '-' + String(mon + 1).padStart(2, '0');
}

/**
 * The calendar day a moment falls on where the code runs: the reader's own
 * day in a browser, UTC on the server.
 *
 * Both used to be read off toISOString(), which is UTC everywhere. In Poland
 * that was an hour or two after midnight; in New York "today" turned into
 * tomorrow at 8 in the evening, and on the 31st the app moved to the next
 * month for a whole evening -- a fill-up or a trade logged after dinner
 * was dated a day late, and an evening's spending filed under the wrong month.
 */
export function localDay(date = new Date()) {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function currentMonth(date = new Date()) {
  return localDay(date).slice(0, 7);
}

export function isMonth(value) {
  return typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

/**
 * A real day, not merely something shaped like one.
 *
 * The shape test alone accepted `2026-13-45`, and the damage was not that the
 * row looked odd: its mirrored ledger line was filed under month `2026-13`,
 * which no month view asks for and the metrics endpoint refuses outright. The
 * money vanished from every month while the vehicle panel went on counting it,
 * so the two halves of one charge disagreed by two hundred zloty and nothing
 * said so.
 *
 * Reachable from the UI only since the edit dialogs gained a typed date field —
 * a picker cannot produce an impossible day, and a text box can.
 */
export function isDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  if (month < 1 || month > 12 || day < 1) return false;

  // Day 0 of the next month is the last day of this one, leap years included.
  return day <= new Date(year, month, 0).getDate();
}

/**
 * Reads a number the way a person actually typed it.
 *
 * `parseFloat` was doing this, and `parseFloat('1,3405')` is **1** -- it stops
 * dead at the separator and hands back a confident, wrong answer. Half of
 * Europe writes the decimal point as a comma, this app ships in Polish, and a
 * cost of 348,50 quietly becoming 348 is the mildest way that goes wrong.
 * `1,3405` ounces becoming `1` is not mild.
 *
 * Digit grouping is stripped: spaces of every width, and apostrophes.
 *
 * Which of `.` and `,` is the decimal point is decided by what is there:
 *   - both present -> the one further right is the decimal point, the other groups
 *   - one of them, once -> decimal point (`1,34`, `1.34`)
 *   - one of them, more than once -> grouping (`1,234,567`, `1.234.567`)
 *
 * Anything that is not a number after that returns null rather than a salvaged
 * prefix. `12x3` is a typo, and 123 is not what it meant.
 *
 * One exception, in English only: a single comma with exactly three digits
 * after it and no leading zero -- `1,750`, `12,500` -- groups thousands. That
 * is how English writes a rent, and reading it as 1.75 stored a hundredth of
 * what an American typed. In Polish `1,750` stays one and three quarters.
 * The app says which language it is reading in with `setNumberReading`.
 */
let commaGroups = false;

/** Which language typed numbers are read in: 'EN' or 'PL' (see the exception above). */
export function setNumberReading(language) {
  commaGroups = language === 'EN';
}

/**
 * A stored number as the language writes it, for seeding an edit field or
 * hinting at one: `42.97` in English, `42,97` in Polish. Polish-style `0,125`
 * in an English field would come back as 125 under the rule above.
 */
export function numberText(value) {
  const text = String(value);
  return commaGroups ? text : text.replace('.', ',');
}

export function parseNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  /* Digit grouping written as a space of any width, or as an apostrophe. */
  const text = String(value ?? '').replace(/[\s   '’]/g, '');
  if (!text) return null;

  const commas = (text.match(/,/g) || []).length;
  const dots = (text.match(/\./g) || []).length;

  let decimal = null;
  let group = null;

  if (commas && dots) {
    decimal = text.lastIndexOf(',') > text.lastIndexOf('.') ? ',' : '.';
    group = decimal === ',' ? '.' : ',';
  } else if (commas === 1) {
    if (commaGroups && !dots && /^[+-]?[1-9]\d{0,2},\d{3}$/.test(text)) group = ',';
    else decimal = ',';
  }
  else if (dots === 1) decimal = '.';
  else if (commas > 1) group = ',';
  else if (dots > 1) group = '.';

  let whole = text;
  let fraction = '';
  if (decimal) {
    const at = text.lastIndexOf(decimal);
    whole = text.slice(0, at);
    fraction = text.slice(at + 1);
    if (!/^\d*$/.test(fraction)) return null;
  }

  if (group) {
    /*
     * Grouping has to look like grouping. Without this check "1..2" reads as a
     * pair of separators, strips to "12", and comes back as a number nobody
     * typed -- which is the same class of silent wrong answer as parseFloat's.
     */
    const parts = whole.replace(/^[+-]/, '').split(group);
    const grouped = parts.length > 1
      && /^\d{1,3}$/.test(parts[0])
      && parts.slice(1).every(part => /^\d{3}$/.test(part));
    if (!grouped) return null;

    whole = whole.split(group).join('');
  }

  if (!/^[+-]?\d*$/.test(whole)) return null;
  if (!/\d/.test(whole + fraction)) return null;

  const parsed = Number((whole || '0') + (fraction ? '.' + fraction : ''));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Whole months from `a` to `b`, signed. `2026-01` -> `2027-01` is 12. */
export function monthsBetween(a, b) {
  const [ya, ma] = String(a).split('-').map(Number);
  const [yb, mb] = String(b).split('-').map(Number);
  return (yb - ya) * 12 + (mb - ma);
}

/**
 * How often a fixed cost repeats, in months.
 *
 * Months, not weeks, because the ledger is monthly — a weekly charge has no
 * month to be filed under and would have to be invented four or five times.
 * Somebody paying weekly enters the monthly total instead, which is what their
 * bank statement shows anyway.
 */
export const CADENCES = [1, 3, 6, 12];

/**
 * Whether a fixed cost falls in this month at all — cadence only, ignoring
 * whether it has already been posted.
 *
 * The cadence is counted from `firstMonth`, so a yearly subscription started in
 * March lands in March. Counting from "now" instead would make the same
 * template due in a different month depending on when it was opened.
 */
export function recurringFallsIn(template, month) {
  if (!isMonth(month)) return false;

  const every = CADENCES.includes(template.everyMonths) ? template.everyMonths : 1;
  const first = isMonth(template.firstMonth) ? template.firstMonth : null;
  const until = isMonth(template.untilMonth) ? template.untilMonth : null;

  /* A template with no start behaves as it always did: every month, forever. */
  if (first && monthsBetween(first, month) < 0) return false;
  if (until && monthsBetween(month, until) < 0) return false;
  if (!first) return every === 1;

  return monthsBetween(first, month) % every === 0;
}

/**
 * Whether pressing "add" would create this entry for this month.
 *
 * Posted months are recorded one by one rather than as "the last one posted".
 * The single-value version could be walked backwards: post September, step back
 * to August, post again, and September — no longer the last — came due a second
 * time. A yearly subscription made that worse, since stepping back a year was
 * enough to charge it twice.
 */
export function recurringDue(template, month, ledger = null) {
  if (!template.active) return false;
  if (!recurringFallsIn(template, month)) return false;
  return !recurringPosted(template, month, ledger);
}

/**
 * Whether the month has this template's entry -- checked against the ledger
 * itself, not only against the record that it was once posted.
 *
 * The record alone said "posted" after the entry had been deleted, so the
 * button asked whether to add the fixed costs *again* when there was nothing
 * there to add again to. With a ledger given, a posted month counts only while
 * its entry is still in it. Without one (older callers) the record decides.
 */
export function recurringPosted(template, month, ledger = null) {
  if (!(template.postedMonths || []).includes(month)) return false;
  return ledger ? recurringEntryIn(template, month, ledger) : true;
}

/**
 * The entry a template posts, found in a month of the ledger.
 *
 * An expense posted since 1.58.1 names its template (`origin: 'recurring'`),
 * which keeps finding it after the template is renamed. The name is the other
 * way in, and the only one for an older expense or any income: an expense
 * carrying the template's name as its note, an income carrying it as its
 * source. A template deleted and put back has a new id, so it relies on the
 * name too. Lines mirrored from the vehicle or a debt payment never count.
 */
export function recurringEntryIn(template, month, ledger) {
  if (template.kind === 'income') {
    return (ledger.income || []).some(row => row.month === month && row.label === template.label);
  }
  return (ledger.expenses || []).some(row => row.month === month
    && (!row.origin || row.origin === 'recurring')
    && ((row.origin === 'recurring' && row.originId === template.id) || row.note === template.label));
}

/**
 * What a repeating charge costs per month, averaged over its cadence.
 *
 * A yearly 600 is 50 a month here. The ledger never sees that number — the
 * entry it posts is the whole 600 in the month it is actually charged. This is
 * only for the "what is committed before I decide anything" figure, where
 * comparing a yearly bill against a monthly one at face value would understate
 * it twelvefold.
 */
export function monthlyEquivalent(template) {
  const every = CADENCES.includes(template.everyMonths) ? template.everyMonths : 1;
  return template.amount / every;
}

/** The next month on or after `from` in which this template falls. Null if it has ended. */
export function nextChargeMonth(template, from) {
  if (!template.active) return null;
  const until = isMonth(template.untilMonth) ? template.untilMonth : null;

  let month = from;
  /* At most one cadence forward: past that the answer is beyond the end date. */
  for (let step = 0; step <= 12; step += 1) {
    if (until && monthsBetween(month, until) < 0) return null;
    if (recurringFallsIn(template, month)) return month;
    month = shiftMonth(month, 1);
  }
  return null;
}

/**
 * The day a month actually has.
 *
 * A charge set for the 31st still happens in April, on the 30th — the bank does
 * not skip the month. Clamping is the only honest reading; refusing the 31st
 * outright would mean nobody could describe a charge that really is on the 31st.
 */
export function clampDay(month, day) {
  const [year, index] = String(month).split('-').map(Number);
  const last = new Date(year, index, 0).getDate();
  return Math.min(Math.max(1, day), last);
}

/** `2026-09` + 4 -> `2026-09-04`, with the day clamped to the month. */
export const dayStamp = (month, day) =>
  `${month}-${String(clampDay(month, day)).padStart(2, '0')}`;

/**
 * When the money next actually leaves, as a day rather than a month.
 *
 * `nextChargeMonth` counts in months, which is all a ledger needs and not
 * enough to say what is coming: a cost charged on the 4th, asked about on the
 * 11th, has already gone this month — its next charge is the 4th of the month
 * after, not a date in the past.
 *
 * Returns the month alone when no day was stated. That is not a failure: most
 * people do not record the day, and "some time in March" is still worth saying.
 */
export function nextChargeDate(template, today = new Date()) {
  if (!template || !template.active) return null;

  const from = currentMonth(today);
  const todayStamp = localDay(today);
  const day = Number(template.dayOfMonth) || null;

  let month = from;
  /* Thirteen steps covers a yearly cadence from any starting month. */
  for (let step = 0; step <= 13; step += 1) {
    const candidate = nextChargeMonth(template, month);
    if (!candidate) return null;
    if (!day) return { month: candidate, date: null };

    const stamp = dayStamp(candidate, day);
    if (stamp >= todayStamp) return { month: candidate, date: stamp };
    month = shiftMonth(candidate, 1);
  }
  return null;
}

/** Whole days from `from` to `to`, both ISO days. Negative when `to` is past. */
export function daysBetween(from, to) {
  const a = Date.parse(from + 'T00:00:00Z');
  const b = Date.parse(to + 'T00:00:00Z');
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86400000);
}

// ------------------------------------------------------------ debt history --

/*
 * A debt's balance, month by month.
 *
 * Every other tab already reads the month on screen -- income and spending by
 * month, holdings from the month they were bought, fuel by the day -- while a
 * debt had one balance, today's, shown in every month alike. Looking back at
 * August said what is owed in September, so nothing on screen could say how
 * much the debt had come down.
 *
 * The history is a short list of recorded balances, one per month at most,
 * carried forward until the next one: the same "in force since" rule the
 * exchange rates follow. A payment writes the month it is logged in; editing
 * the balance writes the month on screen.
 *
 * A debt with no history at all is one written before any of this existed. It
 * reads as it always did -- today's balance in every month -- rather than
 * vanishing.
 */

const debtBalance = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** The recorded balances, oldest first, one per month. */
export function debtHistory(debt) {
  const rows = Array.isArray(debt?.history) ? debt.history : [];
  const byMonth = new Map();
  for (const row of rows) {
    /* Nothing is owed below zero: paying more than the balance clears it. */
    if (isMonth(row?.month)) byMonth.set(row.month, Math.max(0, debtBalance(row.balance)));
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, balance]) => ({ month, balance }));
}

/**
 * The first month the debt is on the books: the agreement's start month when
 * that is earlier than anything recorded, otherwise the first record. Null for
 * a debt with no history, which has no beginning to speak of.
 */
export function debtStartsIn(debt) {
  const history = debtHistory(debt);
  if (!history.length) return null;
  const first = history[0].month;
  return isMonth(debt?.startMonth) && debt.startMonth < first ? debt.startMonth : first;
}

/**
 * What was owed at the end of a month, or null when the debt did not exist yet.
 *
 * Between the start month and the first record, the first recorded balance is
 * the best that is known and is what is shown.
 */
export function debtBalanceAt(debt, month) {
  const history = debtHistory(debt);
  if (!history.length) return debtBalance(debt?.balance);

  const from = debtStartsIn(debt);
  if (month < from) return null;

  let balance = history[0].balance;
  for (const row of history) {
    if (row.month > month) break;
    balance = row.balance;
  }
  return balance;
}

/** Today's balance: the latest record, or the stored figure without history. */
export function debtLatestBalance(debt) {
  const history = debtHistory(debt);
  return history.length ? history[history.length - 1].balance : debtBalance(debt?.balance);
}

/**
 * What a payment in a month can go against: that month's balance -- or, for a
 * month before anything was recorded, the first balance on record. A payment
 * is clamped to this, on the server and in the client alike.
 */
export function debtOwedIn(debt, month) {
  const known = debtHistory(debt);
  if (!known.length) return debtBalance(debt?.balance);
  return debtBalanceAt(debt, month) ?? known[0].balance;
}

/**
 * The history after a balance was set for a month -- an edit, or the balance a
 * debt is entered with. Later months keep what they had: a correction to
 * August is not a statement about October.
 */
export function debtHistoryWithBalance(debt, month, balance) {
  const history = debtHistory(debt).filter(row => row.month !== month);
  history.push({ month, balance: Math.max(0, debtBalance(balance)) });
  return history.sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * The history after a payment is taken back -- the ledger line it posted was
 * deleted, most often because the same payment was logged twice. The inverse
 * of `debtHistoryWithPayment`: the month's balance goes back up, and every
 * later one with it. `amount` is what the payment actually took off, which is
 * what the server records, so the figures return to what they were.
 */
export function debtHistoryWithoutPayment(debt, month, amount) {
  const refunded = Math.max(0, debtBalance(amount));
  const known = debtHistory(debt);
  const base = debtOwedIn(debt, month);

  const history = known
    .filter(row => row.month !== month)
    .map(row => (row.month > month ? { ...row, balance: row.balance + refunded } : row));
  history.push({ month, balance: base + refunded });
  return history.sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * The history after a payment in a month. The month's balance comes down, and
 * so does every later one: money repaid in August is still repaid in October.
 */
export function debtHistoryWithPayment(debt, month, amount) {
  const paid = Math.max(0, debtBalance(amount));
  const known = debtHistory(debt);
  const base = debtOwedIn(debt, month);

  const history = known
    .filter(row => row.month !== month)
    .map(row => (row.month > month ? { ...row, balance: Math.max(0, row.balance - paid) } : row));
  history.push({ month, balance: Math.max(0, base - paid) });
  return history.sort((a, b) => a.month.localeCompare(b.month));
}
